import {
  ProposedRoute,
  RouteBuilderSession,
  RouteGenerationInputs,
} from "../../../../../types/routeBuilder";

export function createSessionId(prefix: string) {
  return `${prefix}-${Math.random().toString(36).slice(2, 10)}`;
}

export function createPropertyVisitId() {
  return createSessionId("property-visit");
}

/** Empty-route drop hint only when a stop can actually be dragged onto it. */
export function shouldShowDropPropertiesHint(
  session: Pick<RouteBuilderSession, "routes" | "unroutedJobs">,
): boolean {
  return session.routes.length > 1 || (session.unroutedJobs?.length ?? 0) > 0;
}

export type PropertySearchRouteChip = {
  id: string;
  name: string;
  color?: string;
};

/** Builder-session routes plus saved company routes, de-duped by name. */
export function propertySearchRouteChips(
  property: {
    jobId: string;
    assignedRoutes?: PropertySearchRouteChip[];
  },
  session: Pick<RouteBuilderSession, "routes">,
): PropertySearchRouteChip[] {
  const chips: PropertySearchRouteChip[] = [];
  const seen = new Set<string>();
  for (const route of session.routes) {
    if (!route.stops.some((stop) => stop.propertyId === property.jobId)) {
      continue;
    }
    seen.add(route.name);
    chips.push({ id: route.id, name: route.name, color: route.color });
  }
  for (const route of property.assignedRoutes ?? []) {
    if (seen.has(route.name)) continue;
    seen.add(route.name);
    chips.push(route);
  }
  return chips;
}

/** Case-insensitive match against property name or address fields. */
export function propertyMatchesSearch(
  property: {
    name?: string;
    address?: Record<string, unknown> | null;
  },
  search: string,
): boolean {
  const needle = search.trim().toLowerCase();
  if (!needle) return true;
  const address = property.address
    ? Object.values(property.address)
        .filter((value): value is string => typeof value === "string")
        .join(" ")
    : "";
  return `${property.name ?? ""} ${address}`.toLowerCase().includes(needle);
}

const MINUTES_PER_HOUR = 60;
const SECONDS_PER_HOUR = 3600;

export const DEFAULT_MAX_ROUTE_DURATION_HOURS = 8;

/** Cap route count at the number of properties selected for generation. */
export function maxAllowedRouteCount(propertyCount: number): number {
  return Math.max(1, propertyCount || 1);
}

/** UI displays hours; persisted inputs and generate-plan still use minutes. */
export function maxRouteDurationHoursFromMinutes(minutes: number): number {
  return Math.max(MINUTES_PER_HOUR, minutes) / MINUTES_PER_HOUR;
}

export function maxRouteDurationMinutesFromHours(hours: number): number {
  return Math.max(1, hours || 1) * MINUTES_PER_HOUR;
}

export function formatMaxRouteDurationHours(minutes: number): string {
  const hours = maxRouteDurationHoursFromMinutes(minutes);
  const value = Number.isInteger(hours)
    ? String(hours)
    : String(Number(hours.toFixed(1)));
  return `${value} ${hours === 1 ? "hour" : "hours"}`;
}

/** Draft list prefix, e.g. "8 hours max · ". Empty when time is off. */
export function formatDraftMaxDuration(
  inputs:
    | (Pick<RouteGenerationInputs, "maxRouteDurationMinutes"> &
        Partial<Pick<RouteGenerationInputs, "incorporateTime">>)
    | undefined,
): string {
  if (
    inputs?.incorporateTime === false ||
    typeof inputs?.maxRouteDurationMinutes !== "number"
  ) {
    return "";
  }
  return `${formatMaxRouteDurationHours(inputs.maxRouteDurationMinutes)} max · `;
}

export function formatEstimatedServiceTimeFromSeconds(seconds: number): string {
  return seconds >= SECONDS_PER_HOUR
    ? formatRouteDurationHoursFromSeconds(seconds)
    : `${Math.round(seconds / 60)} min`;
}

/** Compact route-metric duration (seconds → hours) for list/detail chips. */
export function formatRouteDurationHoursFromSeconds(seconds: number): string {
  const hours = Math.max(0, seconds) / SECONDS_PER_HOUR;
  const rounded = Math.round(hours * 10) / 10;
  const value = Number.isInteger(rounded)
    ? String(rounded)
    : rounded.toFixed(1);
  return `${value} hr`;
}

export type UnassignedFollowUp = "none" | "offer-without-max" | "inform";

/**
 * Retry once with the server's default limit when a requested max was applied.
 */
export function unassignedFollowUp({
  unassignedCount,
  limitedByMaxDuration,
}: {
  unassignedCount: number;
  limitedByMaxDuration: boolean;
}): UnassignedFollowUp {
  if (unassignedCount <= 0) return "none";
  return limitedByMaxDuration ? "offer-without-max" : "inform";
}

export function unassignedMessage(
  followUp: Exclude<UnassignedFollowUp, "none">,
  {
    unassignedCount,
    maxRouteDurationMinutes,
  }: {
    unassignedCount: number;
    maxRouteDurationMinutes: number;
  },
): { title: string; description: string } {
  const one = unassignedCount === 1;
  if (followUp === "offer-without-max") {
    const duration = formatMaxRouteDurationHours(maxRouteDurationMinutes);
    return {
      title: "Some properties don't fit",
      description:
        `${unassignedCount} ${one ? "property doesn't" : "properties don't"} fit within the current limit of ${duration}.` +
        `\n\nDo you want to add ${one ? "it" : "them"} to routes anyway? This rebuilds all routes with no time limit, so some routes may run much longer than ${duration}.` +
        "\nNote: this uses an additional route optimization.",
    };
  }
  return {
    title: "Some properties are unassigned",
    description:
      `${unassignedCount} ${one ? "property doesn't" : "properties don't"} fit the current route or timing parameters.` +
      `\n\nPlease see the Unassigned sidebar and drag ${one ? "it" : "them"} to the appropriate route.`,
  };
}

/** A missing flag (drafts saved before it existed, edit mode) means time counts. */
export function incorporatesTime(
  inputs: Pick<RouteGenerationInputs, "incorporateTime"> | null | undefined,
): boolean {
  return inputs?.incorporateTime !== false;
}

/**
 * Route summary for lists and panels, e.g. "4 stops · 2.5 hr · 10.0 mi".
 * With time off the duration is drive time only, labeled as such, since no
 * service times count.
 */
export function routeSummaryText(
  route: Pick<ProposedRoute, "stops" | "metrics">,
  options: { incorporateTime: boolean; includeDistance?: boolean },
): string {
  const parts = [`${route.stops.length} stops`];
  parts.push(
    options.incorporateTime
      ? formatRouteDurationHoursFromSeconds(route.metrics.totalDurationSeconds)
      : `${formatRouteDurationHoursFromSeconds(
          route.metrics.drivingDurationSeconds,
        )} drive`,
  );
  if (options.includeDistance) {
    parts.push(`${(route.metrics.drivingDistanceMeters / 1609).toFixed(1)} mi`);
  }
  return parts.join(" · ");
}
